






































































































































import CustomFieldBoolean from "@/components/report/CustomFieldBoolean.vue";
import Debug from "@/components/utility/Debug.vue";
import LayoutReport from "@/layouts/LayoutReport.vue";
import TheLayoutBase from "@/layouts/TheLayoutBase.vue";
import { SignatureTokenEnum } from "@/lib/enum/signature-token.enum";
import { isCrossToken, isSignatureToken } from "@/lib/signature-token.utils";
import { handleError } from "@/lib/utility/handleError";
import { ISignDocument } from "@/models/sign-document.entity";
import {
  SignDigitalSignatureRequestSignerViewModelGen,
  SignDocumentTokenDtoGen
} from "@/services/sign/v1/data-contracts";
import { ConfigModule } from "@/store/modules/config";
import { DocumentTemplateModule } from "@/store/modules/document-template.store";
import { mixins } from "vue-class-component";
import { Component, Prop, Ref, Watch } from "vue-property-decorator";
import DocumentDetailCardEditor from "../sign/DocumentDetailCardEditor.vue";
import DocumentDetailEditorMixin from "../sign/DocumentDetailEditorMixin.vue";
import DocumentTemplateDetailPageList from "../sign/DocumentTemplateDetailPageList.vue";
import SignFormEditorCanvas from "./SignFormEditorCanvas.vue";
import SignFormEditorControls from "./SignFormEditorControls.vue";
import SignFormEditorInputList from "./SignFormEditorInputList.vue";
import SignFormSignaturePageListSheet from "./SignFormSignaturePageListSheet.vue";

@Component({
  components: {
    TheLayoutBase,
    LayoutReport,
    Debug,
    DocumentDetailCardEditor,
    SignFormEditorCanvas,
    DocumentTemplateDetailPageList,
    SignFormEditorControls,
    SignFormEditorInputList,
    SignFormSignaturePageListSheet,
    CustomFieldBoolean
  }
})
export default class SignFormEditorMobile extends mixins(DocumentDetailEditorMixin) {
  readonly SignatureTokenEnum = SignatureTokenEnum;

  @Ref("detailCardEditor")
  detailCardEditor!: DocumentDetailCardEditor;

  @Prop()
  document!: ISignDocument;

  @Prop()
  signRequest!: SignDigitalSignatureRequestSignerViewModelGen;

  @Ref("signFormEditorControls")
  signFormEditorControls!: SignFormEditorControls;

  isLoading = false;
  isLoadingDownload = false;

  clientWidth = 0;
  clientHeight = 0;

  readonly PAGE_LIST_ID = "pagelistsheet";

  get isTokenDialogActive() {
    return this.highlightedTokenIndex > -1;
  }
  set isTokenDialogActive(active: boolean) {
    if (!active) {
      DocumentTemplateModule.setHighlightedToken(-1);
    }
  }

  get highlightedTokenIndex() {
    return DocumentTemplateModule.highlightedToken;
  }

  get isHeadless() {
    return ConfigModule.headless;
  }

  get isHorizontalPageList() {
    return this.pages.length > 1 && this.$vuetify.breakpoint.smAndDown;
  }

  get highlightedToken() {
    const index = this.highlightedTokenIndex;
    if (index === -1) {
      return undefined;
    }

    (this.$refs.textField as any)?.focus();

    return DocumentTemplateModule.documentTokens[this.currentPageNumber][index];
  }

  get currentTokenIndex() {
    const allEditableTokens = this.getAllEditableTokens();
    return this.getCurrentTokenGlobalIndex(allEditableTokens);
  }

  get totalTokens() {
    return this.getAllEditableTokens().length;
  }

  async mounted() {
    this.isLoading = true;
    DocumentTemplateModule.setHighlightedToken(-1);
    await this.initialize(this.document).catch(handleError);
    this.isLoading = false;
    this.onResize();
  }

  @Watch("currentPageNumber")
  onPageChange() {
    document.getElementById(`${this.PAGE_LIST_ID}${this.currentPageNumber}`)?.scrollIntoView({ behavior: "smooth" });
  }

  async download() {
    try {
      this.isLoadingDownload = true;
      await this.signFormEditorControls.download();
    } catch (e) {
      this.$log.error(e);
      this.$toast.error(this.$t("components.thg.ThgQuoteCreateView.error"));
    } finally {
      this.isLoadingDownload = false;
    }
  }

  getSelector() {
    return this.detailCardEditor.selector;
  }

  resetHighlightedToken() {
    DocumentTemplateModule.setHighlightedToken(-1);
  }

  goToNextToken() {
    const allEditableTokens = this.getAllEditableTokens();
    const currentGlobalIndex = this.getCurrentTokenGlobalIndex(allEditableTokens);

    if (currentGlobalIndex < allEditableTokens.length - 1) {
      // Go to next token
      const nextToken = allEditableTokens[currentGlobalIndex + 1];
      DocumentTemplateModule.setHighlightedToken(nextToken.index);

      // Switch page if necessary
      if (nextToken.pageIndex !== this.currentPageNumber) {
        this.setCurrentPage(nextToken.pageIndex);
      }
    } else {
      // Last token, close dialog
      this.resetHighlightedToken();
    }
  }

  goToPreviousToken() {
    const allEditableTokens = this.getAllEditableTokens();
    const currentGlobalIndex = this.getCurrentTokenGlobalIndex(allEditableTokens);

    if (currentGlobalIndex > 0) {
      // Go to previous token
      const previousToken = allEditableTokens[currentGlobalIndex - 1];
      DocumentTemplateModule.setHighlightedToken(previousToken.index);

      // Switch page if necessary
      if (previousToken.pageIndex !== this.currentPageNumber) {
        this.setCurrentPage(previousToken.pageIndex);
      }
    }
  }

  getCurrentTokenGlobalIndex(
    allEditableTokens: Array<{ index: number; pageIndex: number; token: SignDocumentTokenDtoGen }>
  ) {
    return allEditableTokens.findIndex(
      token => token.pageIndex === this.currentPageNumber && token.index === this.highlightedTokenIndex
    );
  }

  get isFirstToken() {
    const allEditableTokens = this.getAllEditableTokens();
    const currentGlobalIndex = this.getCurrentTokenGlobalIndex(allEditableTokens);
    return currentGlobalIndex <= 0;
  }

  get isLastToken() {
    const allEditableTokens = this.getAllEditableTokens();
    const currentGlobalIndex = this.getCurrentTokenGlobalIndex(allEditableTokens);
    return currentGlobalIndex >= allEditableTokens.length - 1;
  }

  getAllEditableTokens() {
    const tokens: Array<{ index: number; pageIndex: number; token: SignDocumentTokenDtoGen }> = [];

    DocumentTemplateModule.documentTokens.forEach((page, pageIndex) => {
      page.forEach((token, tokenIndex) => {
        if (token.isEditable && !this.getIsSignature(token.token)) {
          tokens.push({
            index: tokenIndex,
            pageIndex,
            token
          });
        }
      });
    });

    return tokens;
  }

  getIsSignature(token: string) {
    return isSignatureToken(token);
  }

  setCurrentPage(currentPageNumber: number) {
    this.currentPageNumber = currentPageNumber;
    DocumentTemplateModule.setCurrentPage(this.currentPageNumber);
  }

  onResize() {
    this.clientWidth = document.documentElement.clientWidth;
    this.clientHeight = document.documentElement.clientHeight;
  }

  previousPage() {
    if (this.currentPageNumber === 0) {
      this.currentPageNumber = this.pages.length - 1;
      DocumentTemplateModule.setCurrentPage(this.currentPageNumber);

      return;
    }
    this.currentPageNumber = this.currentPageNumber - 1;
    DocumentTemplateModule.setCurrentPage(this.currentPageNumber);
  }

  nextPage() {
    if (this.currentPageNumber === this.pages.length - 1) {
      this.currentPageNumber = 0;
      DocumentTemplateModule.setCurrentPage(this.currentPageNumber);

      return;
    }
    this.currentPageNumber = this.currentPageNumber + 1;
    DocumentTemplateModule.setCurrentPage(this.currentPageNumber);
  }

  isCrossToken(token: string) {
    return isCrossToken(token);
  }

  updateCrossToken(documentToken: SignDocumentTokenDtoGen) {
    documentToken.value = documentToken.value !== SignatureTokenEnum.CROSS_TOKEN ? SignatureTokenEnum.CROSS_TOKEN : "";
  }
}
